import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Search,
  Plus,
  MessageSquare,
  ExternalLink,
  X,
} from 'lucide-react';
import type { ComplianceIncident, IncidentSeverity, IncidentStatus } from '../types/compliance';
import './Incidents.css';

export default function Incidents() {
  const [incidents, setIncidents] = useState<ComplianceIncident[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState<IncidentSeverity | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<IncidentStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<ComplianceIncident | null>(null);
  const [newIncident, setNewIncident] = useState({
    title: '',
    description: '',
    severity: 'warning' as IncidentSeverity,
    framework: '',
    controlId: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchIncidents = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/compliance/incidents');
      if (res.ok) {
        const data = await res.json();
        setIncidents(data);
      } else {
        setError('Failed to load incidents');
      }
    } catch (err) {
      console.error('Failed to fetch incidents:', err);
      setError('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, []);

  const handleCreateIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/incidents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newIncident,
          status: 'open',
          affectedProducts: [],
        }),
      });
      if (res.ok) {
        setShowCreateModal(false);
        setNewIncident({ title: '', description: '', severity: 'warning', framework: '', controlId: '' });
        fetchIncidents();
      }
    } catch (err) {
      console.error('Failed to create incident:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (incidentId: string, newStatus: IncidentStatus) => {
    try {
      const res = await fetch(`/api/compliance/incidents/${incidentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        fetchIncidents();
        if (selectedIncident?.id === incidentId) {
          const updated = await res.json();
          setSelectedIncident(updated);
        }
      }
    } catch (err) {
      console.error('Failed to update incident:', err);
    }
  };

  const filteredIncidents = incidents.filter((incident) => {
    const matchesSearch =
      incident.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      incident.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeverity = selectedSeverity === 'all' || incident.severity === selectedSeverity;
    const matchesStatus = selectedStatus === 'all' || incident.status === selectedStatus;
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const getSeverityIcon = (severity: IncidentSeverity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle size={18} className="severity-icon critical" />;
      case 'error': return <XCircle size={18} className="severity-icon error" />;
      case 'warning': return <AlertCircle size={18} className="severity-icon warning" />;
      default: return <AlertCircle size={18} className="severity-icon info" />;
    }
  };

  const getSeverityClass = (severity: IncidentSeverity) => {
    switch (severity) {
      case 'critical': return 'badge-critical';
      case 'error': return 'badge-failing';
      case 'warning': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getStatusClass = (status: IncidentStatus) => {
    switch (status) {
      case 'resolved':
      case 'closed': return 'status-resolved';
      case 'in_progress': return 'status-progress';
      case 'acknowledged': return 'status-acknowledged';
      default: return 'status-open';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  const getTimeToSLA = (slaDeadline?: string) => {
    if (!slaDeadline) return null;
    const diff = new Date(slaDeadline).getTime() - Date.now();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 0) return { text: 'Overdue', urgent: true };
    if (hours < 4) return { text: `${hours}h remaining`, urgent: true };
    if (hours < 24) return { text: `${hours}h remaining`, urgent: false };
    const days = Math.floor(hours / 24);
    return { text: `${days}d remaining`, urgent: false };
  };

  const stats = {
    total: incidents.length,
    open: incidents.filter((i) => i.status === 'open').length,
    critical: incidents.filter((i) => i.severity === 'critical' && i.status !== 'resolved' && i.status !== 'closed').length,
    resolved: incidents.filter((i) => i.status === 'resolved' || i.status === 'closed').length,
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Compliance Incidents</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#a6adc8' }}>
          Loading incidents...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Compliance Incidents</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Compliance Incidents</h1>
          <p>Track and resolve compliance-related incidents</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} />
          Report Incident
        </button>
      </div>

      {/* Stats */}
      <div className="incident-stats">
        <div className="incident-stat">
          <span className="incident-stat-value">{stats.total}</span>
          <span className="incident-stat-label">Total Incidents</span>
        </div>
        <div className="incident-stat open">
          <AlertCircle size={18} />
          <span className="incident-stat-value">{stats.open}</span>
          <span className="incident-stat-label">Open</span>
        </div>
        <div className="incident-stat critical">
          <AlertTriangle size={18} />
          <span className="incident-stat-value">{stats.critical}</span>
          <span className="incident-stat-label">Critical</span>
        </div>
        <div className="incident-stat resolved">
          <CheckCircle size={18} />
          <span className="incident-stat-value">{stats.resolved}</span>
          <span className="incident-stat-label">Resolved</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="incidents-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search incidents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={selectedSeverity}
            onChange={(e) => setSelectedSeverity(e.target.value as IncidentSeverity | 'all')}
            className="filter-select"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as IncidentStatus | 'all')}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Incidents List */}
      <div className="incidents-list">
        {filteredIncidents.map((incident) => {
          const slaInfo = getTimeToSLA(incident.slaDeadline);

          return (
            <div key={incident.id} className={`incident-card ${incident.severity}`}>
              <div className="incident-card-header">
                <div className="incident-severity-section">
                  {getSeverityIcon(incident.severity)}
                  <span className={`badge ${getSeverityClass(incident.severity)}`}>
                    {incident.severity}
                  </span>
                  <span className={`incident-status ${getStatusClass(incident.status)}`}>
                    {incident.status.replace('_', ' ')}
                  </span>
                </div>
                <span className="incident-created">{formatTimeAgo(incident.createdAt)}</span>
              </div>

              <h3 className="incident-title">{incident.title}</h3>
              <p className="incident-description">{incident.description}</p>

              {incident.controlId && (
                <div className="incident-control">
                  <span className="incident-control-id">{incident.controlId}</span>
                  <span className="incident-control-name">{incident.controlName}</span>
                  {incident.framework && (
                    <span className="incident-framework">{incident.framework}</span>
                  )}
                </div>
              )}

              <div className="incident-meta">
                {incident.assignee && (
                  <div className="incident-meta-item">
                    <User size={14} />
                    <span>{incident.assignee}</span>
                  </div>
                )}
                {slaInfo && (
                  <div className={`incident-meta-item ${slaInfo.urgent ? 'urgent' : ''}`}>
                    <Clock size={14} />
                    <span>{slaInfo.text}</span>
                  </div>
                )}
                <div className="incident-meta-item">
                  <AlertCircle size={14} />
                  <span>{incident.affectedProducts.length} products affected</span>
                </div>
              </div>

              <div className="incident-products">
                {incident.affectedProducts.map((product) => (
                  <span key={product} className="product-tag">{product}</span>
                ))}
              </div>

              <div className="incident-actions">
                <button className="btn btn-secondary" onClick={() => setSelectedIncident(incident)}>
                  <ExternalLink size={16} />
                  View Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredIncidents.length === 0 && (
        <div className="empty-state">
          <AlertTriangle size={48} />
          <h3>No incidents found</h3>
          <p>All clear! No matching incidents.</p>
        </div>
      )}

      {/* Create Incident Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Report Incident</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateIncident}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={newIncident.title}
                  onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                  placeholder="Brief description of the incident"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newIncident.description}
                  onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                  placeholder="Detailed description of what happened..."
                  rows={4}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Severity</label>
                  <select
                    value={newIncident.severity}
                    onChange={(e) => setNewIncident({ ...newIncident, severity: e.target.value as IncidentSeverity })}
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Framework (optional)</label>
                  <select
                    value={newIncident.framework}
                    onChange={(e) => setNewIncident({ ...newIncident, framework: e.target.value })}
                  >
                    <option value="">Select framework</option>
                    <option value="SOC2">SOC2</option>
                    <option value="GDPR">GDPR</option>
                    <option value="HIPAA">HIPAA</option>
                    <option value="COPPA">COPPA</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Control ID (optional)</label>
                <input
                  type="text"
                  value={newIncident.controlId}
                  onChange={(e) => setNewIncident({ ...newIncident, controlId: e.target.value })}
                  placeholder="e.g., CC-1.1"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Incident'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Incident Detail Modal */}
      {selectedIncident && (
        <div className="modal-overlay" onClick={() => setSelectedIncident(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                {getSeverityIcon(selectedIncident.severity)}
                <h2>{selectedIncident.title}</h2>
              </div>
              <button className="close-btn" onClick={() => setSelectedIncident(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`incident-status ${getStatusClass(selectedIncident.status)}`}>
                    {selectedIncident.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Severity</span>
                  <span className={`badge ${getSeverityClass(selectedIncident.severity)}`}>
                    {selectedIncident.severity}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created</span>
                  <span>{new Date(selectedIncident.createdAt).toLocaleString()}</span>
                </div>
                {selectedIncident.assignee && (
                  <div className="detail-item">
                    <span className="detail-label">Assignee</span>
                    <span>{selectedIncident.assignee}</span>
                  </div>
                )}
              </div>
              <div className="detail-section">
                <h3>Description</h3>
                <p>{selectedIncident.description}</p>
              </div>
              {selectedIncident.controlId && (
                <div className="detail-section">
                  <h3>Related Control</h3>
                  <div className="incident-control">
                    <span className="incident-control-id">{selectedIncident.controlId}</span>
                    <span className="incident-control-name">{selectedIncident.controlName}</span>
                    {selectedIncident.framework && (
                      <span className="incident-framework">{selectedIncident.framework}</span>
                    )}
                  </div>
                </div>
              )}
              {selectedIncident.affectedProducts.length > 0 && (
                <div className="detail-section">
                  <h3>Affected Products</h3>
                  <div className="incident-products">
                    {selectedIncident.affectedProducts.map((product) => (
                      <span key={product} className="product-tag">{product}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="detail-section">
                <h3>Update Status</h3>
                <div className="status-actions">
                  {selectedIncident.status === 'open' && (
                    <button className="btn btn-secondary" onClick={() => handleUpdateStatus(selectedIncident.id, 'acknowledged')}>
                      Acknowledge
                    </button>
                  )}
                  {(selectedIncident.status === 'open' || selectedIncident.status === 'acknowledged') && (
                    <button className="btn btn-secondary" onClick={() => handleUpdateStatus(selectedIncident.id, 'in_progress')}>
                      Start Progress
                    </button>
                  )}
                  {selectedIncident.status !== 'resolved' && selectedIncident.status !== 'closed' && (
                    <button className="btn btn-success" onClick={() => handleUpdateStatus(selectedIncident.id, 'resolved')}>
                      <CheckCircle size={14} />
                      Mark Resolved
                    </button>
                  )}
                  {selectedIncident.status === 'resolved' && (
                    <button className="btn btn-secondary" onClick={() => handleUpdateStatus(selectedIncident.id, 'closed')}>
                      Close Incident
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
